import { useMemo } from 'react'
import { cn } from '@duck/ui'
import { useTar, type TranslationKey } from '@/modules/i18n'
import { useFormatDuration } from './useFormatDuration'
import { SEVERITY_BADGE_CLASS, SEVERITY_COLOR } from './severity'
import { activePosition, isInsert, typingMs, type Ev, type SessionSplit } from './sessions'
import { dayShort, hhmm } from './format'

const W = 820
const H = 320
const L = 44
const R = 12
const T = 40
const B = 44
const GAP_W = 26
// Each paste takes a fixed slice of the x axis, so a burst of pastes reads as a staircase instead of one line.
const PASTE_W = 14
const PASTED_FILL = 'hsl(22, 85%, 58%)'


interface Pin { x: number; y: number; e: Ev; afterAway?: number }

const MIN_SESSION_MS = 60_000

export const ActivityGrowthChart = ({ split: { sessions, breaks } }: { split: SessionSplit }) => {
  const { t } = useTar()
  const formatDuration = useFormatDuration()
  // The SVG is a string built from every event; rebuild only when the data or language changes.
  const { svg, pins } = useMemo(() => {
    const totalActive = Math.max(1, sessions.reduce((a, s) => a + Math.max(MIN_SESSION_MS, s.activeMs), 0))
    const totalInserts = sessions.reduce((a, s) => a + s.events.filter(isInsert).length, 0)
    const plotW = W - L - R - GAP_W * breaks.length - PASTE_W * totalInserts
    const total = sessions.reduce((a, s) => a + s.typed + s.pasted, 0)
    const maxY = Math.max(100, Math.ceil(total / 100) * 100)
    const y = (v: number) => T + (H - T - B) * (1 - v / maxY)
    const base = H - B
    const gridStep = maxY <= 500 ? 100 : maxY <= 2000 ? 250 : Math.ceil(maxY / 5 / 100) * 100
    const gridLines: number[] = []
    for (let v = 0; v <= maxY; v += gridStep) gridLines.push(v)

    const shapes: string[] = []
    const pins: Pin[] = []
    let x0 = L
    let typed = 0
    let pasted = 0
    sessions.forEach((s, i) => {
      const sessionMs = Math.max(MIN_SESSION_MS, s.activeMs)
      const inserts = s.events.filter(isInsert)
      const typingW = plotW * sessionMs / totalActive
      const sw = typingW + PASTE_W * inserts.length
      const xOf = (tm: number) => x0 + typingW * activePosition(s, tm) / sessionMs + PASTE_W * inserts.filter((p) => p.t < tm).length
      // Typing ramps linearly across its stretch; a paste is a vertical step.
      const pts: { x: number; typed: number; pasted: number }[] = [{ x: x0, typed, pasted }]
      s.events.forEach((e, k) => {
        if (e.type === 'active') { pts.push({ x: xOf(e.t), typed, pasted }); typed += e.len ?? 0; pts.push({ x: xOf(e.t + typingMs(e, s.events, k)), typed, pasted }); return }
        const ax = xOf(e.t)
        if (isInsert(e)) {
          pts.push({ x: ax, typed, pasted })
          pasted += e.len ?? 0
          pts.push({ x: ax, typed, pasted })
          if (e.label !== 'info') {
            const prev = s.events[k - 1]
            pins.push({ x: ax + PASTE_W / 2, y: y(typed + pasted), e, afterAway: prev !== undefined && prev.type === 'focus' ? prev.awayMs : undefined })
          }
        }
        if (e.type === 'submit') shapes.push(`<line x1="${ax}" x2="${ax}" y1="${T}" y2="${base}" stroke="hsl(var(--color-card-foreground))" stroke-width="3"/>`)
      })
      pts.push({ x: x0 + sw, typed, pasted })
      const stepped = (key: 'typed' | 'total') => pts.map((p, k) => `${k === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${y(key === 'typed' ? p.typed : p.typed + p.pasted).toFixed(1)}`).join(' ')
      shapes.unshift(
        `<path d="${stepped('total')} V${base} H${x0.toFixed(1)} Z" fill="url(#growth-pasted)"/>`,
        `<path d="${stepped('typed')} V${base} H${x0.toFixed(1)} Z" fill="url(#growth-typed)"/>`
      )
      shapes.push(
        `<text x="${x0 + 2}" y="${T - 10}" fill="hsl(var(--color-muted-foreground))" font-size="13">${t('adminActivityReport.growth.sessionStart', { time: `${dayShort(s.start)} ${hhmm(s.start)}` })}</text>`,
        `<text x="${x0 + 2}" y="${base + 16}" fill="hsl(var(--color-muted-foreground))" font-size="13">${t('adminActivityReport.ledger.activeMinutes', { n: s.activeMinutes })}</text>`
      )
      x0 += sw
      if (breaks[i] !== undefined) {
        shapes.push(
          `<rect x="${x0}" y="${T}" width="${GAP_W}" height="${base - T}" fill="hsl(var(--color-background))"/>`,
          `<line x1="${x0 + GAP_W / 2}" x2="${x0 + GAP_W / 2}" y1="${T}" y2="${base}" stroke="hsl(var(--color-muted-foreground))" stroke-dasharray="3 4"/>`,
          `<text x="${x0 + GAP_W / 2}" y="${base + 36}" fill="hsl(var(--color-muted-foreground))" font-size="13" text-anchor="middle">${t('adminActivityReport.ledger.awayFor', { duration: formatDuration(breaks[i]) })}</text>`
        )
        x0 += GAP_W
      }
    })
    pins.forEach((p, i) => {
      const c = SEVERITY_COLOR[p.e.label]
      shapes.push(`<line x1="${p.x}" x2="${p.x}" y1="${p.y}" y2="${p.y - 22}" stroke="${c}"/><circle cx="${p.x}" cy="${p.y - 30}" r="10" fill="${c}"/><text x="${p.x}" y="${p.y - 26}" fill="#111" font-size="15" font-weight="600" text-anchor="middle">${i + 1}</text>`)
    })
    const defs = `<defs>
      <linearGradient id="growth-typed" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="hsl(var(--color-primary))" stop-opacity=".95"/><stop offset="1" stop-color="hsl(var(--color-primary))" stop-opacity=".55"/></linearGradient>
      <linearGradient id="growth-pasted" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${PASTED_FILL}" stop-opacity=".95"/><stop offset="1" stop-color="${PASTED_FILL}" stop-opacity=".5"/></linearGradient>
    </defs>`
    const yAxisLabel = `<text x="${L - 8}" y="${T - 10}" fill="hsl(var(--color-muted-foreground))" font-size="13" text-anchor="end">${t('adminActivityReport.growth.yAxis')}</text>`
    const grid = yAxisLabel + gridLines.map((v) => `<line x1="${L}" x2="${W - R}" y1="${y(v)}" y2="${y(v)}" stroke="hsl(var(--color-border))" opacity=".6"/><text x="${L - 8}" y="${y(v) + 4}" fill="hsl(var(--color-muted-foreground))" font-size="13" text-anchor="end">${v}</text>`).join('')
    return { svg: defs + grid + shapes.join(''), pins }
  }, [sessions, breaks, t, formatDuration])

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-border bg-card p-4">
        {/* Hardcoded SVG built from numbers and translated strings only; chart never renders user content. */}
        <svg viewBox={`0 0 ${W} ${H}`} className="block h-auto w-full" style={{ direction: 'ltr' }} role="img" aria-label={t('adminActivityReport.growth.title')} dangerouslySetInnerHTML={{ __html: svg }} />
        <div className="mt-2 flex flex-wrap items-center gap-5 text-xs text-muted-foreground">
          <span><i className="me-1.5 inline-block h-2.5 w-3 rounded-sm bg-primary align-[-1px]" />{t('adminActivityReport.growth.typed')}</span>
          <span><i className="me-1.5 inline-block h-2.5 w-3 rounded-sm align-[-1px]" style={{ background: PASTED_FILL }} />{t('adminActivityReport.growth.pasted')}</span>
          <span><i className="me-1.5 inline-block h-2.5 w-3 rounded-sm border border-dashed border-muted-foreground align-[-1px]" />{t('adminActivityReport.growth.break')}</span>
          <span><i className="me-1.5 inline-block h-2.5 w-0.5 bg-foreground align-[-1px]" />{t('adminActivityReport.types.submit')}</span>
        </div>
      </div>
      {pins.length > 0 ? (
        <div className="grid gap-3 md:grid-cols-2">
          {pins.map((p, i) => (
            <div key={i} className="relative rounded-lg border border-border bg-card px-3.5 py-3">
              <span className="absolute -top-2.5 start-3 grid h-5 w-5 place-items-center rounded-full text-xs font-semibold text-neutral-900" style={{ background: SEVERITY_COLOR[p.e.label] }}>{i + 1}</span>
              <h3 className="mb-1 mt-1 text-sm font-medium">
                {t(`adminActivityReport.ledger.${p.e.type as 'paste' | 'drop' | 'inject'}`, { n: p.e.len ?? 0, surface: p.e.surface !== undefined ? t(`adminActivityReport.surfaces.${p.e.surface}`) : '' })}
                <span className={cn('ms-2 whitespace-nowrap rounded px-2 py-0.5 text-xs', SEVERITY_BADGE_CLASS[p.e.label])}>{t(`adminActivityReport.severity.${p.e.label}`)}</span>
              </h3>
              <p className="mb-2 text-[13px] text-muted-foreground">
                {/* unknown rules render their raw key by design */}
                {p.e.rule !== undefined ? `${t(`adminActivityReport.rules.${p.e.rule}` as TranslationKey)}. ` : ''}
                {p.e.replacedAll === true ? `${t('adminActivityReport.growth.replacedAll')}. ` : ''}
                {p.e.pauseMs !== undefined && p.e.pauseMs > 0
                  ? (p.afterAway !== undefined
                    ? t('adminActivityReport.ledger.afterReturn', { duration: formatDuration(p.e.pauseMs), away: formatDuration(p.afterAway) })
                    : t('adminActivityReport.ledger.afterPrevious', { duration: formatDuration(p.e.pauseMs) })) + '. '
                  : ''}
                <span dir="ltr">{new Date(p.e.t).toLocaleTimeString()}</span>
              </p>
              {p.e.preview !== undefined ? <pre className="overflow-hidden whitespace-pre-wrap rounded bg-foreground/10 px-2.5 py-2 font-mono text-xs text-foreground" dir="ltr">{p.e.preview}</pre> : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
